/**
 * Start all / Stop all of one project, from its `GET /api/v1/projects/:id/services` answer (SandboxService
 * getProjectServices): the Projects list's row button and bulk actions use these, the dashboard's RunAllActions does
 * the same per service.
 */
import config from '../../config';
import { isRunning, sessionIdFor, stopService } from '../../services/SandboxService';
import { START_POLL_MS, START_TIMEOUT_MS, startServices } from './startProgress';
import { primaryService } from '../../utils/projectOverview';
import { isNoBuild } from '../../utils/repoFlags';
import { parseRunnableName, runnableServiceName } from '../../utils/runnablesModel';
import { isCliItem } from '../../utils/cliServices';

/** Up and done building: what "running" means for a project's count. */
const upAndReady = (service) => isRunning(service) && service?.buildStatus !== 'building';

/**
 * Whether a service's own session serves its app: it runs and its build neither failed nor crashed (a failed build's
 * session may still answer with the runner's own page, "API Portal · No API endpoints were found", not the app).
 */
export const servesItsOwn = (service) => isRunning(service) && !['failed', 'error'].includes(service?.buildStatus) && !service?.crashed;

/** The service a pinned file's session belongs to (`services/app` for `services/app--compose-…`), or null. */
export const ownerOfSession = (serviceName) => parseRunnableName(serviceName)?.service || null;

/**
 * The project's run items with each service a pinned file of it carries left out: a service whose own process does not
 * run while one of its pinned files' sessions does (a folder with no stack of its own, or a failed build, next to a
 * compose file that runs) is not a service to start, stop or count on its own: the pinned session stands for it, and
 * Open app opens it. The rule picked: a running pinned session wins over its owner unless the owner's own session
 * serves its app (runs, build not failed); while nothing of it runs the owner counts.
 */
export function foldPinned(items) {
  const carried = new Set(items.filter((item) => ownerOfSession(item.serviceName) && upAndReady(item.service)).map((item) => ownerOfSession(item.serviceName)));
  return items.filter((item) => !(carried.has(item.serviceName) && !servesItsOwn(item.service)));
}

/**
 * One item per service of the project: its own services (`serviceNames`, never started ones too) and any the runner
 * knows besides (a pinned file's session too), each with the runner's entry or null. `[{ serviceName, service }]`.
 * A service carried by its running pinned file is left out (foldPinned).
 */
export function runItems(answer) {
  if (!answer) { return []; }
  const services = Array.isArray(answer.services) ? answer.services : [];
  const names = [...new Set([...(answer.serviceNames || []), ...services.map((service) => service.serviceName)])];
  return foldPinned(names.map((serviceName) => ({ serviceName, service: services.find((service) => service.serviceName === serviceName) || null })));
}

/**
 * The items Start all / Stop all and the run state work on, from the project's services (`items`, `[{ serviceName,
 * service }]`): with the sessions of the files pinned in them that the runner knows (started before: Start all starts
 * them again, Stop all stops them) and the default app when it is a pinned file (`defaultRunnable`, started with its
 * payload); a service carried by its running pinned file left out (foldPinned).
 */
export function projectRunItems(items, runnerServices = [], defaultRunnable = null) {
  const owners = new Set(items.map((item) => item.serviceName));
  const known = (runnerServices || []).filter((entry) => owners.has(ownerOfSession(entry.serviceName)) && !items.some((item) => item.serviceName === entry.serviceName))
    .map((entry) => ({ serviceName: entry.serviceName, service: entry }));
  return foldPinned(withDefaultRunnable([...items, ...known], defaultRunnable, runnerServices));
}

/**
 * The items that count for "Running 5/5" and Start all's wait: without the command-line tools (utils/cliServices.js),
 * which are built, never running.
 */
export const countedItems = (projectId, items) => (items || []).filter((item) => !isCliItem(projectId, item));

/** `{ running, total, crashed, building }` of a project's answer (command-line tools left out). */
export function runSummary(answer) {
  const items = countedItems(answer?.projectId, runItems(answer));
  return {
    running: items.filter(({ service }) => isRunning(service)).length,
    total: items.length,
    crashed: items.some(({ service }) => Boolean(service) && (service.crashed || service.buildStatus === 'failed')),
    building: items.some(({ service }) => service?.buildStatus === 'building'),
  };
}

/**
 * Starts every service of the project that does not run and waits until all of them really run (see
 * `startServices`: the runner's answer to a start only means it began). `onProgress(progress)` follows each service;
 * `onEach()` after each start request and each look at them (the caller refreshes its list). A plan's running-services
 * limit stops the sequence with its own message. Resolves to `{ error, progress }`, `error` null when every service runs.
 * A service known to be No build (context only, utils/repoFlags.js) is skipped: vfs-service and the runner refuse it.
 */
export async function startProject(projectId, allItems, { onEach, onProgress, cancelled, shouldStop, pollMs, timeoutMs } = {}) {
  const items = allItems.filter(({ serviceName }) => !isNoBuild(projectId, serviceName));
  let first = true;
  const progress = await startServices(projectId, items, {
    cancelled,
    shouldStop,
    pollMs,
    timeoutMs,
    onProgress: (next) => {
      onProgress?.(next);
      if (!first) { onEach?.(); }
      first = false;
    },
  });
  if (progress.error) { return { error: progress.error, progress }; }
  const failed = progress.services.filter((service) => service.phase === 'failed');
  if (failed.length) {
    return { error: failed.map((service) => `${service.serviceName} could not start: ${service.reason}`).join('\n'), progress };
  }
  return { error: null, progress };
}

/** Stops the services that run (only those). Resolves to `{ error }`, null when all went well. */
export async function stopProject(projectId, items, { onEach } = {}) {
  let error = null;
  for (const { serviceName, service } of items.filter((item) => isRunning(item.service))) {
    try {
      // eslint-disable-next-line no-await-in-loop
      await stopService(service.sessionId || sessionIdFor(projectId, serviceName));
    } catch (err) {
      error = `Could not stop ${serviceName}: ${err?.message || 'unknown error'}`;
    }
    onEach?.();
  }
  return { error };
}

/**
 * Where a running service's app opens, as the header's "Open App" does (the runner's `previewUrl`: the path-based
 * `/api/preview/<project>/<service>/proxy/`), made absolute on Flow's public origin. null when it does not run.
 */
export function appUrlOf(projectId, item) {
  if (!item || !isRunning(item.service) || item.service.buildStatus === 'building') { return null; }
  const path = item.service.previewUrl || `/api/preview/${encodeURIComponent(projectId)}/${item.serviceName}/proxy/`;
  if (/^https?:\/\//.test(path)) { return path; }
  const origin = (config.publicBaseUrl || window.location.origin).replace(/\/$/, '');
  return `${origin}${path.startsWith('/') ? '' : '/'}${path}`;
}

const isNamed = (item, name) => Boolean(name) && String(item?.serviceName || '').replace(/^services\//, '') === name;

/**
 * The app to open for a project: the project's default app (utils/defaultApp, `preferred`) when it runs, else its
 * primary service (the first frontend) when it runs, else the first running one.
 */
export function appItem(projectId, items, preferred = null) {
  const runningItems = items.filter((item) => appUrlOf(projectId, item));
  if (!runningItems.length) { return null; }
  const chosen = runningItems.find((item) => isNamed(item, preferred));
  if (chosen) { return chosen; }
  const primary = primaryService(runningItems.map((item) => [item.serviceName, { name: item.serviceName }]));
  return runningItems.find((item) => item.serviceName === primary?.key) || runningItems[0];
}

/**
 * `items` with the project's default app when it is a file pinned in a service (`runnable`, utils/defaultApp
 * `{ service, kind, file }`): its session (`services/app--compose-docker-compose-yaml`, with the runner's entry from
 * `runnerServices` when it has one) and its `runnable`, so appItem / primaryItem find it and a Start sends its payload
 * (the same as Apps' Start). An item already there for it gets its `runnable`. `items` itself without one.
 */
export function withDefaultRunnable(items, runnable, runnerServices = []) {
  if (!runnable?.service || !runnable.kind || !runnable.file) { return items; }
  const serviceName = runnableServiceName(runnable.service, runnable);
  const pinned = { service: runnable.service, kind: runnable.kind, file: runnable.file };
  if (items.some((item) => item.serviceName === serviceName)) {
    return items.map((item) => (item.serviceName === serviceName ? { ...item, runnable: item.runnable || pinned } : item));
  }
  const service = (runnerServices || []).find((entry) => entry.serviceName === serviceName) || null;
  return [...items, { serviceName, service, runnable: pinned }];
}

/** Opens a running service's app in a new tab. */
export function openApp(projectId, item) {
  const url = appUrlOf(projectId, item);
  if (url) { window.open(url, '_blank', 'noopener'); }
  return url;
}

/** The service a project's "Open app" means: its default app (`preferred`), else its primary one (the first frontend), running or not. */
export function primaryItem(items, preferred = null) {
  if (!items.length) { return null; }
  const chosen = items.find((item) => isNamed(item, preferred));
  if (chosen) { return chosen; }
  const primary = primaryService(items.map((item) => [item.serviceName, { name: item.serviceName }]));
  return items.find((item) => item.serviceName === primary?.key) || items[0];
}

/**
 * "Open app" on a project that is stopped: starts its app, then opens it. The tab is opened at once (a tab opened
 * after the wait would be blocked as a pop-up) and goes to the app when it answers. Resolves to the app's URL; throws
 * when it cannot start (the tab is closed).
 */
export async function startAndOpenApp(projectId, item, { onChanged, pollMs = START_POLL_MS, timeoutMs = START_TIMEOUT_MS } = {}) {
  const tab = window.open('', '_blank');
  try {
    if (tab?.document) { tab.document.title = 'Starting…'; tab.document.body.textContent = `Starting ${item.serviceName.replace(/^services\//, '')}…`; }
  } catch { /* another origin: nothing to write */ }
  try {
    const progress = await startServices(projectId, [item], { pollMs, timeoutMs, onProgress: () => onChanged?.() });
    const entry = progress.services[0];
    if (entry.phase !== 'running') {
      throw new Error(entry.phase === 'limit' ? entry.reason : `${item.serviceName} could not start: ${entry.reason || 'see Logs'}`);
    }
    const url = appUrlOf(projectId, { serviceName: item.serviceName, service: { containerStatus: 'running', buildStatus: 'ready' } });
    if (tab) { tab.opener = null; tab.location.href = url; }
    onChanged?.();
    return url;
  } catch (error) {
    tab?.close();
    onChanged?.();
    throw error;
  }
}
